// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { mockSites } from '../data/sitesData';
import { useSiteRestriction } from '../hooks/useSiteRestriction';

// Extended shipment interface with detail data
interface ShipmentDetail {
  id: string;
  origin: string;
  destination: string;
  status: 'planned' | 'loading' | 'in_transit' | 'delivered' | 'delayed';
  eta: string;
  pallets: number;
  fillRate: number;
  // Extended details
  division: 'Chilled' | 'Frozen' | 'Ambient' | 'Fresh';
  carrier: string;
  driver: string;
  driverPhone: string;
  vehicleReg: string;
  vehicleType: string;
  temperature?: string;
  departureTime: string;
  estimatedArrival: string;
  distance: string;
  currentLocation: string;
  progressPercent: number;
  items: { sku: string; description: string; quantity: number; weight: string }[];
  timeline: { time: string; event: string; status: 'completed' | 'current' | 'pending' }[];
  notes?: string;
}

// Mock data for demonstration with extended details - US locations
const mockShipments: ShipmentDetail[] = [
  {
    id: 'SHP-001',
    origin: 'Chicago Distribution Hub',
    destination: 'Atlanta Fulfillment Center',
    status: 'in_transit',
    eta: '14:30',
    pallets: 24,
    fillRate: 92,
    division: 'Chilled',
    carrier: 'Swift Transport USA',
    driver: 'James Wilson',
    driverPhone: '+1 (312) 555-0123',
    vehicleReg: 'IL-SW21-TRN',
    vehicleType: 'Refrigerated Semi',
    temperature: '35-39°F',
    departureTime: '06:45',
    estimatedArrival: '14:30',
    distance: '720 miles',
    currentLocation: 'I-65 South, Near Louisville, KY',
    progressPercent: 68,
    items: [
      { sku: 'CHI-001', description: 'Fresh Dairy Products', quantity: 8, weight: '5,280 lbs' },
      { sku: 'CHI-002', description: 'Chilled Ready Meals', quantity: 10, weight: '3,960 lbs' },
      { sku: 'CHI-003', description: 'Fresh Produce', quantity: 6, weight: '2,640 lbs' },
    ],
    timeline: [
      { time: '06:30', event: 'Order confirmed and assigned', status: 'completed' },
      { time: '06:45', event: 'Departed Chicago Distribution Hub', status: 'completed' },
      { time: '09:15', event: 'Checkpoint: Indianapolis, IN', status: 'completed' },
      { time: '11:30', event: 'Driver break (45 mins)', status: 'completed' },
      { time: '14:30', event: 'Estimated arrival Atlanta', status: 'pending' },
    ],
    notes: 'Priority delivery - customer requested early slot',
  },
  {
    id: 'SHP-002',
    origin: 'Dallas Regional Warehouse',
    destination: 'Phoenix Fulfillment Hub',
    status: 'delivered',
    eta: '11:45',
    pallets: 26,
    fillRate: 100,
    division: 'Ambient',
    carrier: 'Express Freight USA',
    driver: 'Sarah Thompson',
    driverPhone: '+1 (214) 555-0456',
    vehicleReg: 'TX-EF19-FRT',
    vehicleType: 'Dry Van Semi',
    departureTime: '05:00',
    estimatedArrival: '11:45',
    distance: '870 miles',
    currentLocation: 'Phoenix Fulfillment Hub - Bay 4',
    progressPercent: 100,
    items: [
      { sku: 'AMB-101', description: 'Canned Goods', quantity: 12, weight: '7,920 lbs' },
      { sku: 'AMB-102', description: 'Dry Groceries', quantity: 8, weight: '4,400 lbs' },
      { sku: 'AMB-103', description: 'Household Products', quantity: 6, weight: '3,080 lbs' },
    ],
    timeline: [
      { time: '04:45', event: 'Order confirmed and assigned', status: 'completed' },
      { time: '05:00', event: 'Departed Dallas Regional Warehouse', status: 'completed' },
      { time: '08:30', event: 'Arrived Phoenix Fulfillment Hub', status: 'completed' },
      { time: '11:30', event: 'Unloading completed', status: 'completed' },
      { time: '11:45', event: 'POD signed - Delivery complete', status: 'completed' },
    ],
  },
  {
    id: 'SHP-003',
    origin: 'Los Angeles Cross-Dock',
    destination: 'Phoenix Fulfillment Hub',
    status: 'delayed',
    eta: '16:00',
    pallets: 18,
    fillRate: 69,
    division: 'Frozen',
    carrier: 'Arctic Logistics USA',
    driver: 'Michael Davies',
    driverPhone: '+1 (310) 555-0789',
    vehicleReg: 'CA-AL20-FRZ',
    vehicleType: 'Deep Freeze Semi',
    temperature: '0°F to -4°F',
    departureTime: '10:30',
    estimatedArrival: '16:00',
    distance: '370 miles',
    currentLocation: 'I-10 East - Traffic Incident near Quartzsite, AZ',
    progressPercent: 35,
    items: [
      { sku: 'FRZ-201', description: 'Frozen Meat Products', quantity: 8, weight: '6,160 lbs' },
      { sku: 'FRZ-202', description: 'Ice Cream & Desserts', quantity: 6, weight: '3,080 lbs' },
      { sku: 'FRZ-203', description: 'Frozen Vegetables', quantity: 4, weight: '1,980 lbs' },
    ],
    timeline: [
      { time: '10:15', event: 'Order confirmed and assigned', status: 'completed' },
      { time: '10:30', event: 'Departed Los Angeles Cross-Dock', status: 'completed' },
      { time: '12:45', event: 'Checkpoint: Palm Springs, CA', status: 'completed' },
      { time: '13:30', event: 'DELAY: Traffic incident I-10', status: 'current' },
      { time: '16:00', event: 'Revised ETA Phoenix', status: 'pending' },
    ],
    notes: 'Delay due to accident on I-10. Driver safe, alternative route being assessed.',
  },
  {
    id: 'SHP-004',
    origin: 'Newark Cold Storage',
    destination: 'Atlanta Fulfillment Center',
    status: 'planned',
    eta: '17:30',
    pallets: 22,
    fillRate: 85,
    division: 'Ambient',
    carrier: 'Eastern Haulage',
    driver: 'Robert Brown',
    driverPhone: '+1 (973) 555-0234',
    vehicleReg: 'NJ-MH22-LOG',
    vehicleType: 'Dry Van Semi',
    departureTime: '15:00',
    estimatedArrival: '17:30',
    distance: '870 miles',
    currentLocation: 'Newark Cold Storage - Loading Bay 7',
    progressPercent: 0,
    items: [
      { sku: 'AMB-301', description: 'Beverages', quantity: 10, weight: '7,040 lbs' },
      { sku: 'AMB-302', description: 'Snacks & Confectionery', quantity: 8, weight: '3,960 lbs' },
      { sku: 'AMB-303', description: 'Personal Care', quantity: 4, weight: '1,760 lbs' },
    ],
    timeline: [
      { time: '14:00', event: 'Order confirmed and assigned', status: 'completed' },
      { time: '14:30', event: 'Vehicle inspection complete', status: 'completed' },
      { time: '15:00', event: 'Scheduled departure', status: 'pending' },
      { time: '17:30', event: 'Estimated arrival Atlanta', status: 'pending' },
    ],
  },
  {
    id: 'SHP-005',
    origin: 'Newark Cold Storage',
    destination: 'Chicago Distribution Hub',
    status: 'in_transit',
    eta: '15:15',
    pallets: 26,
    fillRate: 100,
    division: 'Chilled',
    carrier: 'Northeast Haulage',
    driver: 'Andrew Scott',
    driverPhone: '+1 (973) 555-0567',
    vehicleReg: 'NJ-NH21-CHL',
    vehicleType: 'Refrigerated Semi',
    temperature: '35-39°F',
    departureTime: '08:00',
    estimatedArrival: '15:15',
    distance: '790 miles',
    currentLocation: 'I-80 West, Near Toledo, OH',
    progressPercent: 45,
    items: [
      { sku: 'CHI-101', description: 'Fresh Seafood Products', quantity: 10, weight: '5,720 lbs' },
      { sku: 'CHI-102', description: 'Dairy & Cheese', quantity: 8, weight: '4,840 lbs' },
      { sku: 'CHI-103', description: 'Fresh Salads', quantity: 8, weight: '3,520 lbs' },
    ],
    timeline: [
      { time: '07:30', event: 'Order confirmed and assigned', status: 'completed' },
      { time: '08:00', event: 'Departed Newark Cold Storage', status: 'completed' },
      { time: '10:45', event: 'Checkpoint: Scranton, PA', status: 'completed' },
      { time: '12:30', event: 'Current location: Near Toledo, OH', status: 'current' },
      { time: '15:15', event: 'Estimated arrival Chicago', status: 'pending' },
    ],
  },
  {
    id: 'SHP-006',
    origin: 'Miami Import Warehouse',
    destination: 'Atlanta Fulfillment Center',
    status: 'loading',
    eta: '18:00',
    pallets: 20,
    fillRate: 77,
    division: 'Fresh',
    carrier: 'Fresh Direct USA',
    driver: 'Emma Johnson',
    driverPhone: '+1 (305) 555-0890',
    vehicleReg: 'FL-FD22-FSH',
    vehicleType: 'Refrigerated Semi',
    temperature: '39-46°F',
    departureTime: '15:30',
    estimatedArrival: '18:00',
    distance: '660 miles',
    currentLocation: 'Miami Import Warehouse - Loading Bay 3',
    progressPercent: 0,
    items: [
      { sku: 'FSH-401', description: 'Fresh Tropical Fruit', quantity: 8, weight: '3,960 lbs' },
      { sku: 'FSH-402', description: 'Fresh Vegetables', quantity: 6, weight: '3,080 lbs' },
      { sku: 'FSH-403', description: 'Fresh Herbs & Salads', quantity: 6, weight: '2,200 lbs' },
    ],
    timeline: [
      { time: '14:00', event: 'Order confirmed and assigned', status: 'completed' },
      { time: '14:30', event: 'Loading started', status: 'current' },
      { time: '15:30', event: 'Scheduled departure', status: 'pending' },
      { time: '18:00', event: 'Estimated arrival Atlanta', status: 'pending' },
    ],
    notes: 'Fresh produce - handle with care, maintain cold chain',
  },
];

// Extended alert interface with read/status fields
interface AlertDetail {
  id: number;
  type: 'delay' | 'capacity' | 'performance' | 'temperature' | 'compliance';
  severity: 'high' | 'medium' | 'low';
  message: string;
  time: string;
  isRead: boolean;
  status: 'open' | 'acknowledged' | 'resolved';
  // Extended details for modal
  description: string;
  affectedEntity: string;
  entityType: 'shipment' | 'site' | 'supplier' | 'vehicle';
  createdAt: string;
  updatedAt?: string;
  assignedTo?: string;
  actions: string[];
  relatedAlerts?: number[];
}

const initialAlerts: AlertDetail[] = [
  {
    id: 1,
    type: 'delay',
    severity: 'high',
    message: 'SHP-003 delayed - Traffic congestion on I-10',
    time: '12:45',
    isRead: false,
    status: 'open',
    description: 'Shipment SHP-003 is currently delayed due to a traffic incident on Interstate 10 near Quartzsite, AZ. Driver Michael Davies has reported standstill traffic following a multi-vehicle collision. Alternative routes are being assessed.',
    affectedEntity: 'SHP-003',
    entityType: 'shipment',
    createdAt: '2026-01-10 12:45:00',
    assignedTo: 'Operations Team',
    actions: [
      'Notified customer of potential delay',
      'Driver instructed to take US-60 alternative route',
      'Temperature monitoring increased to 15-minute intervals',
    ],
  },
  {
    id: 2,
    type: 'capacity',
    severity: 'medium',
    message: 'Newark Cold Storage approaching capacity (95%)',
    time: '12:30',
    isRead: false,
    status: 'open',
    description: 'Newark Cold Storage warehouse capacity has reached 95%, which is above the 90% threshold. Inbound shipments may experience delays in unloading. Consider diverting non-urgent deliveries to Atlanta Fulfillment Center which is at 90% capacity.',
    affectedEntity: 'Newark Cold Storage',
    entityType: 'site',
    createdAt: '2026-01-10 12:30:00',
    assignedTo: 'Site Manager - Newark',
    actions: [
      'Expedited outbound shipments to free up space',
      'Contacted Atlanta FC for overflow capacity',
    ],
  },
  {
    id: 3,
    type: 'performance',
    severity: 'low',
    message: 'Supplier ABC below DOT threshold (88%)',
    time: '11:15',
    isRead: true,
    status: 'acknowledged',
    description: 'Supplier ABC has fallen below the 90% Delivery On Time (DOT) threshold with a current score of 88%. This is the second consecutive week of underperformance. A review meeting has been scheduled.',
    affectedEntity: 'Supplier ABC',
    entityType: 'supplier',
    createdAt: '2026-01-10 11:15:00',
    updatedAt: '2026-01-10 11:45:00',
    assignedTo: 'Procurement Team',
    actions: [
      'Performance review meeting scheduled for Jan 12',
      'Alternative supplier options being evaluated',
    ],
  },
  {
    id: 4,
    type: 'temperature',
    severity: 'high',
    message: 'Temperature excursion detected - SHP-005 chilled cargo',
    time: '10:30',
    isRead: true,
    status: 'resolved',
    description: 'A brief temperature excursion was detected in SHP-005 chilled cargo compartment. Temperature rose to 43°F for approximately 8 minutes during a scheduled driver rest stop. Driver has confirmed refrigeration unit was reset and temperature is now stable at 37°F.',
    affectedEntity: 'SHP-005',
    entityType: 'shipment',
    createdAt: '2026-01-10 10:30:00',
    updatedAt: '2026-01-10 10:55:00',
    assignedTo: 'Quality Assurance',
    actions: [
      'Driver contacted and situation assessed',
      'Refrigeration unit reset successfully',
      'Temperature now within acceptable range',
      'Incident logged for carrier review',
    ],
  },
  {
    id: 5,
    type: 'compliance',
    severity: 'medium',
    message: 'Driver hours approaching limit - Vehicle NJ-NH21-CHL',
    time: '09:45',
    isRead: false,
    status: 'open',
    description: 'Driver Andrew Scott (Vehicle NJ-NH21-CHL) has 1 hour 15 minutes remaining of legal driving time for today. Estimated journey completion requires 1 hour 45 minutes. A mandatory rest break will be required before destination arrival.',
    affectedEntity: 'NJ-NH21-CHL',
    entityType: 'vehicle',
    createdAt: '2026-01-10 09:45:00',
    assignedTo: 'Fleet Management',
    actions: [
      'Driver notified of hours remaining',
      'Rest stop location identified',
      'Customer notified of updated ETA',
    ],
  },
];

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    planned: 'bg-blue-100 text-blue-800',
    loading: 'bg-purple-100 text-purple-800',
    in_transit: 'bg-yellow-100 text-yellow-800',
    delivered: 'bg-green-100 text-green-800',
    delayed: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
};

// Network map data - sites and active routes
interface NetworkSite {
  id: string;
  name: string;
  type: 'dc' | 'site';
  city: string;
  lat: number; // latitude
  lng: number; // longitude
  status: 'operational' | 'busy' | 'maintenance';
  capacity?: number;
}

interface NetworkRoute {
  id: string;
  from: string;
  to: string;
  shipmentId: string;
  status: 'in_transit' | 'delayed';
  progress: number;
}

// US sites derived from shared sites data
const networkSites: NetworkSite[] = mockSites.map(site => ({
  id: site.id.toLowerCase(),
  name: site.name,
  type: site.type === 'distribution_center' || site.type === 'cross_dock' ? 'dc' : 'site',
  city: site.address.city,
  lat: site.coordinates.lat,
  lng: site.coordinates.lng,
  status: site.status === 'operational' ? 'operational' : site.status === 'maintenance' ? 'maintenance' : 'busy',
  capacity: Math.round((site.capacity.used / site.capacity.total) * 100),
}));

// Routes between US sites
const networkRoutes: NetworkRoute[] = [
  { id: 'route-1', from: 'site001', to: 'site002', shipmentId: 'SHP-001', status: 'in_transit', progress: 68 }, // Chicago to Atlanta
  { id: 'route-2', from: 'site003', to: 'site008', shipmentId: 'SHP-003', status: 'delayed', progress: 35 }, // LA to Phoenix
  { id: 'route-3', from: 'site004', to: 'site001', shipmentId: 'SHP-005', status: 'in_transit', progress: 45 }, // Newark to Chicago
];

const ControlTower: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const cameFromAlerts = useRef(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDetail | null>(null);
  const [hoveredSite, setHoveredSite] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<AlertDetail[]>(initialAlerts);
  const [alertFilter, setAlertFilter] = useState<'all' | 'unread' | 'open'>('all');
  const [selectedAlert, setSelectedAlert] = useState<AlertDetail | null>(null);

  // Site restriction hook for Site Managers
  const { isRestricted, restrictedSiteId } = useSiteRestriction();

  // Filter sites and routes based on restriction
  const filteredNetworkSites = useMemo(() => {
    if (isRestricted && restrictedSiteId) {
      return networkSites.filter(site => site.id === restrictedSiteId.toLowerCase());
    }
    return networkSites;
  }, [isRestricted, restrictedSiteId]);

  // Filter routes to only include routes involving the restricted site
  const filteredNetworkRoutes = useMemo(() => {
    if (isRestricted && restrictedSiteId) {
      const siteIdLower = restrictedSiteId.toLowerCase();
      return networkRoutes.filter(route => route.from === siteIdLower || route.to === siteIdLower);
    }
    return networkRoutes;
  }, [isRestricted, restrictedSiteId]);

  // Filter shipments to only include those going to/from the restricted site
  const siteRestrictedShipments = useMemo(() => {
    if (isRestricted && restrictedSiteId) {
      const restrictedSite = mockSites.find(s => s.id === restrictedSiteId);
      if (restrictedSite) {
        return mockShipments.filter(shipment =>
          shipment.origin.toLowerCase().includes(restrictedSite.name.toLowerCase()) ||
          shipment.destination.toLowerCase().includes(restrictedSite.name.toLowerCase())
        );
      }
    }
    return mockShipments;
  }, [isRestricted, restrictedSiteId]);

  // Handle URL query params for deep linking from Alerts (vehicle or driver)
  useEffect(() => {
    const vehicleId = searchParams.get('vehicle');
    const driverId = searchParams.get('driver');

    if (vehicleId || driverId) {
      // Find shipment by vehicle registration or driver name
      const shipment = mockShipments.find(s => {
        if (vehicleId) {
          // Match vehicle ID (e.g., TRK-0089 might match part of vehicleReg)
          return s.vehicleReg.toLowerCase().includes(vehicleId.toLowerCase()) ||
                 vehicleId.toLowerCase().includes(s.vehicleReg.toLowerCase());
        }
        if (driverId) {
          // Match driver ID or name
          return s.driver.toLowerCase().includes(driverId.toLowerCase()) ||
                 driverId.toLowerCase().includes(s.driver.toLowerCase());
        }
        return false;
      });

      if (shipment) {
        cameFromAlerts.current = true;
        setSelectedShipment(shipment);
      }
      // Clear the query param after handling
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Handle closing shipment detail - return to Alerts if came from there
  const handleCloseShipmentDetail = () => {
    setSelectedShipment(null);
    if (cameFromAlerts.current) {
      cameFromAlerts.current = false;
      navigate('/alerts');
    }
  };

  // Alert actions
  const markAlertAsRead = (alertId: number) => {
    setAlerts(prev => prev.map(alert =>
      alert.id === alertId ? { ...alert, isRead: true } : alert
    ));
  };

  const clearAlert = (alertId: number) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const acknowledgeAlert = (alertId: number) => {
    setAlerts(prev => prev.map(alert =>
      alert.id === alertId ? { ...alert, status: 'acknowledged', isRead: true } : alert
    ));
  };

  const resolveAlert = (alertId: number) => {
    setAlerts(prev => prev.map(alert =>
      alert.id === alertId ? { ...alert, status: 'resolved', isRead: true } : alert
    ));
  };

  // Filter alerts based on selected filter
  const filteredAlerts = alerts.filter(alert => {
    if (alertFilter === 'unread') return !alert.isRead;
    if (alertFilter === 'open') return alert.status === 'open';
    return true;
  });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate summary stats
  const stats = {
    planned: siteRestrictedShipments.filter(s => s.status === 'planned').length,
    loading: siteRestrictedShipments.filter(s => s.status === 'loading').length,
    inTransit: siteRestrictedShipments.filter(s => s.status === 'in_transit').length,
    delivered: siteRestrictedShipments.filter(s => s.status === 'delivered').length,
    delayed: siteRestrictedShipments.filter(s => s.status === 'delayed').length,
    total: siteRestrictedShipments.length,
  };

  const filteredShipments = selectedFilter === 'all'
    ? siteRestrictedShipments
    : siteRestrictedShipments.filter(s => s.status === selectedFilter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Control Tower
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Real-time logistics operations monitoring
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-mono font-bold text-teal-600">
              {currentTime.toLocaleTimeString()}
            </div>
            <div className="text-sm text-gray-500">
              {currentTime.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-gray-400 cursor-pointer transition hover:shadow-md ${selectedFilter === 'all' ? 'ring-2 ring-gray-400' : ''}`}
          onClick={() => setSelectedFilter('all')}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Shipments</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-blue-500 cursor-pointer transition hover:shadow-md ${selectedFilter === 'planned' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setSelectedFilter(selectedFilter === 'planned' ? 'all' : 'planned')}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">Planned</p>
          <p className="text-3xl font-bold text-blue-600">{stats.planned}</p>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-purple-500 cursor-pointer transition hover:shadow-md ${selectedFilter === 'loading' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setSelectedFilter(selectedFilter === 'loading' ? 'all' : 'loading')}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading</p>
          <p className="text-3xl font-bold text-purple-600">{stats.loading}</p>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-yellow-500 cursor-pointer transition hover:shadow-md ${selectedFilter === 'in_transit' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setSelectedFilter(selectedFilter === 'in_transit' ? 'all' : 'in_transit')}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">In Transit</p>
          <p className="text-3xl font-bold text-yellow-600">{stats.inTransit}</p>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-green-500 cursor-pointer transition hover:shadow-md ${selectedFilter === 'delivered' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setSelectedFilter(selectedFilter === 'delivered' ? 'all' : 'delivered')}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">Delivered</p>
          <p className="text-3xl font-bold text-green-600">{stats.delivered}</p>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border-l-4 border-red-500 cursor-pointer transition hover:shadow-md ${selectedFilter === 'delayed' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setSelectedFilter(selectedFilter === 'delayed' ? 'all' : 'delayed')}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">Delayed</p>
          <p className="text-3xl font-bold text-red-600">{stats.delayed}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Live Shipments Table */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Live Shipments
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Route</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">ETA</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Fill Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredShipments.map((shipment) => (
                  <tr
                    key={shipment.id}
                    onClick={() => setSelectedShipment(shipment)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer group"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-teal-600 group-hover:text-teal-700">{shipment.id}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-white">{shipment.origin}</div>
                      <div className="text-xs text-gray-500">→ {shipment.destination}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={shipment.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">{shipment.eta}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${shipment.fillRate >= 90 ? 'bg-green-500' : shipment.fillRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${shipment.fillRate}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">{shipment.fillRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Active Alerts
              </h3>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {alerts.filter(a => a.status === 'open').length} Open
                </span>
                {alerts.filter(a => !a.isRead).length > 0 && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {alerts.filter(a => !a.isRead).length} Unread
                  </span>
                )}
              </div>
            </div>
            {/* Filter Tabs */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setAlertFilter('all')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  alertFilter === 'all'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                All ({alerts.length})
              </button>
              <button
                onClick={() => setAlertFilter('unread')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  alertFilter === 'unread'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Unread ({alerts.filter(a => !a.isRead).length})
              </button>
              <button
                onClick={() => setAlertFilter('open')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  alertFilter === 'open'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Open ({alerts.filter(a => a.status === 'open').length})
              </button>
            </div>
          </div>
          <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
            {filteredAlerts.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No alerts match the current filter
              </p>
            ) : (
              filteredAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border-l-4 cursor-pointer transition hover:shadow-md ${
                    alert.severity === 'high'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-500'
                      : alert.severity === 'medium'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                  } ${!alert.isRead ? 'ring-2 ring-offset-1 ring-teal-500/30' : ''}`}
                  onClick={() => {
                    setSelectedAlert(alert);
                    markAlertAsRead(alert.id);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      {!alert.isRead && (
                        <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                      )}
                      <span className={`text-xs font-medium uppercase ${
                        alert.severity === 'high' ? 'text-red-600 dark:text-red-400' :
                        alert.severity === 'medium' ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-blue-600 dark:text-blue-400'
                      }`}>
                        {alert.type}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        alert.status === 'open'
                          ? 'bg-red-200 text-red-800 dark:bg-red-800/50 dark:text-red-300'
                          : alert.status === 'acknowledged'
                          ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800/50 dark:text-yellow-300'
                          : 'bg-green-200 text-green-800 dark:bg-green-800/50 dark:text-green-300'
                      }`}>
                        {alert.status}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{alert.time}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{alert.message}</p>
                  {/* Action buttons */}
                  <div className="flex items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                    {!alert.isRead && (
                      <button
                        onClick={() => markAlertAsRead(alert.id)}
                        className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 font-medium"
                      >
                        Mark read
                      </button>
                    )}
                    <button
                      onClick={() => clearAlert(alert.id)}
                      className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 font-medium"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Network Map */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Network Map
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Live view of distribution centers, sites, and active shipments</p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-teal-500"></div>
              <span className="text-gray-600 dark:text-gray-300">DC</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-gray-600 dark:text-gray-300">Site</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse"></div>
              <span className="text-gray-600 dark:text-gray-300">In Transit</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-gray-600 dark:text-gray-300">Delayed</span>
            </div>
          </div>
        </div>
        <div className="relative h-96 overflow-hidden">
          <MapContainer
            center={[39.0, -98.0]}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Route Lines */}
            {filteredNetworkRoutes.map((route) => {
              const fromSite = networkSites.find(s => s.id === route.from);
              const toSite = networkSites.find(s => s.id === route.to);
              if (!fromSite || !toSite) return null;

              // Calculate vehicle position along route
              const vehicleLat = fromSite.lat + (toSite.lat - fromSite.lat) * (route.progress / 100);
              const vehicleLng = fromSite.lng + (toSite.lng - fromSite.lng) * (route.progress / 100);

              return (
                <React.Fragment key={route.id}>
                  {/* Route line */}
                  <Polyline
                    positions={[[fromSite.lat, fromSite.lng], [toSite.lat, toSite.lng]]}
                    pathOptions={{
                      color: route.status === 'delayed' ? '#ef4444' : '#14b8a6',
                      weight: 3,
                      dashArray: '10, 5',
                      opacity: 0.7,
                    }}
                  />
                  {/* Vehicle marker */}
                  <CircleMarker
                    center={[vehicleLat, vehicleLng]}
                    radius={8}
                    pathOptions={{
                      fillColor: route.status === 'delayed' ? '#ef4444' : '#f59e0b',
                      fillOpacity: 1,
                      color: '#fff',
                      weight: 2,
                    }}
                  >
                    <Tooltip permanent direction="right" offset={[10, 0]} className="leaflet-tooltip-custom">
                      <span className={`font-medium ${route.status === 'delayed' ? 'text-red-600' : 'text-teal-600'}`}>
                        {route.shipmentId}
                      </span>
                    </Tooltip>
                  </CircleMarker>
                </React.Fragment>
              );
            })}

            {/* Site Markers */}
            {filteredNetworkSites.map((site) => {
              const fillColor = site.type === 'dc'
                ? site.status === 'maintenance' ? '#f97316' : '#14b8a6'
                : site.status === 'busy' ? '#f59e0b' : '#3b82f6';

              return (
                <CircleMarker
                  key={site.id}
                  center={[site.lat, site.lng]}
                  radius={10}
                  pathOptions={{
                    fillColor: fillColor,
                    fillOpacity: 1,
                    color: '#fff',
                    weight: 3,
                  }}
                  eventHandlers={{
                    mouseover: () => setHoveredSite(site.id),
                    mouseout: () => setHoveredSite(null),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -10]}>
                    <div className="p-1">
                      <p className="font-semibold text-gray-900 text-sm">{site.name}</p>
                      <div className="mt-1 space-y-0.5">
                        <div className="flex justify-between gap-4 text-xs">
                          <span className="text-gray-500">Type:</span>
                          <span className={`font-medium ${site.type === 'dc' ? 'text-teal-600' : 'text-blue-600'}`}>
                            {site.type === 'dc' ? 'Distribution Center' : 'Delivery Site'}
                          </span>
                        </div>
                        <div className="flex justify-between gap-4 text-xs">
                          <span className="text-gray-500">Status:</span>
                          <span className={`font-medium ${
                            site.status === 'operational' ? 'text-green-600' :
                            site.status === 'busy' ? 'text-amber-600' : 'text-orange-600'
                          }`}>
                            {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                          </span>
                        </div>
                        {site.capacity && (
                          <div className="flex justify-between gap-4 text-xs">
                            <span className="text-gray-500">Capacity:</span>
                            <span className={`font-medium ${
                              site.capacity >= 90 ? 'text-red-600' :
                              site.capacity >= 75 ? 'text-amber-600' : 'text-green-600'
                            }`}>
                              {site.capacity}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Map Legend - positioned over the map */}
          <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 border border-gray-200 dark:border-gray-700 z-[1000]">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Active Routes</p>
            <div className="space-y-1">
              {networkRoutes.map((route) => {
                const shipment = mockShipments.find(s => s.id === route.shipmentId);
                return (
                  <div
                    key={route.id}
                    onClick={() => shipment && setSelectedShipment(shipment)}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                  >
                    <div className={`w-2 h-2 rounded-full ${route.status === 'delayed' ? 'bg-red-500' : 'bg-yellow-500'} animate-pulse`}></div>
                    <span className="text-gray-600 dark:text-gray-300">{route.shipmentId}</span>
                    <span className={`${route.status === 'delayed' ? 'text-red-600' : 'text-green-600'}`}>
                      {route.progress}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-lg">
              <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">DOT Today</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">94.2%</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">COT Today</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">91.8%</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Fill Rate</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">87.2%</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pallets Today</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">1,247</p>
            </div>
          </div>
        </div>
      </div>

      {/* Shipment Detail Modal */}
      {selectedShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  selectedShipment.status === 'delivered' ? 'bg-green-100 dark:bg-green-900/30' :
                  selectedShipment.status === 'delayed' ? 'bg-red-100 dark:bg-red-900/30' :
                  selectedShipment.status === 'in_transit' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                  selectedShipment.status === 'loading' ? 'bg-purple-100 dark:bg-purple-900/30' :
                  'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <svg className={`w-8 h-8 ${
                    selectedShipment.status === 'delivered' ? 'text-green-600' :
                    selectedShipment.status === 'delayed' ? 'text-red-600' :
                    selectedShipment.status === 'in_transit' ? 'text-yellow-600' :
                    selectedShipment.status === 'loading' ? 'text-purple-600' :
                    'text-blue-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {selectedShipment.id}
                    </h2>
                    <StatusBadge status={selectedShipment.status} />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {selectedShipment.origin} → {selectedShipment.destination}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseShipmentDetail}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Progress Bar */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-300">Journey Progress</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{selectedShipment.progressPercent}%</span>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      selectedShipment.status === 'delayed' ? 'bg-red-500' :
                      selectedShipment.status === 'delivered' ? 'bg-green-500' :
                      'bg-teal-500'
                    }`}
                    style={{ width: `${selectedShipment.progressPercent}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Current Location: <span className="font-medium text-gray-700 dark:text-gray-300">{selectedShipment.currentLocation}</span>
                </p>
              </div>

              {/* Key Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{selectedShipment.pallets}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pallets</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className={`text-2xl font-bold ${
                    selectedShipment.fillRate >= 90 ? 'text-green-600' :
                    selectedShipment.fillRate >= 70 ? 'text-yellow-600' : 'text-red-600'
                  }`}>{selectedShipment.fillRate}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Fill Rate</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{selectedShipment.distance}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Distance</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-teal-600">{selectedShipment.eta}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">ETA</p>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Shipment Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Shipment Details</h3>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Division</span>
                      <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                        selectedShipment.division === 'Chilled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' :
                        selectedShipment.division === 'Frozen' ? 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300' :
                        selectedShipment.division === 'Ambient' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' :
                        'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                      }`}>{selectedShipment.division}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Carrier</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedShipment.carrier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Vehicle</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedShipment.vehicleReg}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Vehicle Type</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedShipment.vehicleType}</span>
                    </div>
                    {selectedShipment.temperature && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Temperature</span>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{selectedShipment.temperature}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Departed</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedShipment.departureTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">ETA</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedShipment.estimatedArrival}</span>
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Driver Information</h3>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                        <span className="text-xl font-bold text-teal-600">
                          {selectedShipment.driver.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedShipment.driver}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{selectedShipment.carrier}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <a
                        href={`tel:${selectedShipment.driverPhone}`}
                        className="flex items-center gap-2 p-2 bg-white dark:bg-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-500 transition"
                      >
                        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedShipment.driverPhone}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Journey Timeline</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <div className="space-y-4">
                    {selectedShipment.timeline.map((event, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${
                            event.status === 'completed' ? 'bg-green-500' :
                            event.status === 'current' ? 'bg-yellow-500 animate-pulse' :
                            'bg-gray-300 dark:bg-gray-500'
                          }`} />
                          {index < selectedShipment.timeline.length - 1 && (
                            <div className={`w-0.5 h-8 ${
                              event.status === 'completed' ? 'bg-green-300 dark:bg-green-700' :
                              'bg-gray-200 dark:bg-gray-600'
                            }`} />
                          )}
                        </div>
                        <div className="flex-1 pb-2">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium ${
                              event.status === 'completed' ? 'text-gray-900 dark:text-white' :
                              event.status === 'current' ? 'text-yellow-700 dark:text-yellow-400' :
                              'text-gray-500 dark:text-gray-400'
                            }`}>
                              {event.event}
                            </p>
                            <span className={`text-xs font-mono ${
                              event.status === 'current' ? 'text-yellow-600 dark:text-yellow-400 font-bold' :
                              'text-gray-500 dark:text-gray-400'
                            }`}>
                              {event.time}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cargo Manifest</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase">SKU</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase">Description</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase">Qty (Pallets)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase">Weight</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {selectedShipment.items.map((item) => (
                        <tr key={item.sku}>
                          <td className="px-4 py-3 text-sm font-mono text-teal-600">{item.sku}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{item.description}</td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white">{item.quantity}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600 dark:text-gray-300">{item.weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              {selectedShipment.notes && (
                <div className={`rounded-xl p-4 ${
                  selectedShipment.status === 'delayed'
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                }`}>
                  <p className={`text-sm font-medium ${
                    selectedShipment.status === 'delayed'
                      ? 'text-red-800 dark:text-red-300'
                      : 'text-blue-800 dark:text-blue-300'
                  }`}>
                    <strong>Notes:</strong> {selectedShipment.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex gap-4">
                <button className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Track on map
                </button>
                <button className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View documents
                </button>
              </div>
              <button
                onClick={handleCloseShipmentDetail}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  selectedAlert.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30' :
                  selectedAlert.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                  'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <svg className={`w-8 h-8 ${
                    selectedAlert.severity === 'high' ? 'text-red-600' :
                    selectedAlert.severity === 'medium' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Alert #{selectedAlert.id}
                    </h2>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full uppercase ${
                      selectedAlert.severity === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                      selectedAlert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                      'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                    }`}>
                      {selectedAlert.severity}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      selectedAlert.status === 'open'
                        ? 'bg-red-200 text-red-800 dark:bg-red-800/50 dark:text-red-300'
                        : selectedAlert.status === 'acknowledged'
                        ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800/50 dark:text-yellow-300'
                        : 'bg-green-200 text-green-800 dark:bg-green-800/50 dark:text-green-300'
                    }`}>
                      {selectedAlert.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 capitalize">
                    {selectedAlert.type} Alert
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedAlert(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Alert Message */}
              <div className={`rounded-xl p-4 ${
                selectedAlert.severity === 'high'
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  : selectedAlert.severity === 'medium'
                  ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                  : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
              }`}>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {selectedAlert.message}
                </p>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Description</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {selectedAlert.description}
                </p>
              </div>

              {/* Alert Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Affected Entity</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{selectedAlert.affectedEntity}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{selectedAlert.entityType}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Assigned To</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{selectedAlert.assignedTo || 'Unassigned'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Created At</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{selectedAlert.createdAt}</p>
                </div>
                {selectedAlert.updatedAt && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Last Updated</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{selectedAlert.updatedAt}</p>
                  </div>
                )}
              </div>

              {/* Actions Taken */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Actions Taken</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <ul className="space-y-2">
                    {selectedAlert.actions.map((action, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex gap-3">
                {selectedAlert.status === 'open' && (
                  <button
                    onClick={() => {
                      acknowledgeAlert(selectedAlert.id);
                      setSelectedAlert({ ...selectedAlert, status: 'acknowledged', isRead: true });
                    }}
                    className="px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 rounded-lg transition-colors"
                  >
                    Acknowledge
                  </button>
                )}
                {selectedAlert.status !== 'resolved' && (
                  <button
                    onClick={() => {
                      resolveAlert(selectedAlert.id);
                      setSelectedAlert({ ...selectedAlert, status: 'resolved', isRead: true });
                    }}
                    className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/30 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                  >
                    Mark Resolved
                  </button>
                )}
                <button
                  onClick={() => {
                    clearAlert(selectedAlert.id);
                    setSelectedAlert(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/30 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                >
                  Clear Alert
                </button>
              </div>
              <button
                onClick={() => setSelectedAlert(null)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlTower;
